/**
 * Page layout wrappers for conversation and admin screens:
 * `<ConversationScreen>`, `<AdminScreen>`, `<NewPublicFooter>`.
 *
 * `<ConversationScreen>` is the standard conversation layout: a scrollable
 * column capped at 600 px, centred with 40 px padding when not narrow, with a
 * rounded card border on wide viewports. `useIsInSidebar` suppresses the
 * wrapper and padding so the component fits cleanly into hosted contexts.
 * `<AdminScreen>` is a capability-restricted
 * scrollable page that exposes `AdminScreen.Header` and `AdminScreen.Body`
 * sub-components for a 1360 px max-width layout.
 */

import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import type { PropsWithChildren } from 'react';
import { useContext, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useIsInSidebar } from 'util/datastore';
import { DatastoreContext } from 'util/datastoreContext';
import { t } from 'util/stdtypes';

import { RestrictedScreen } from './admin';
import { AdaptiveWidth, Center, HorizBox, Pad, PadBox } from './basics';
import { UtilityText, WebLinkTextButton } from './text';

export type ConversationScreenProps = {
    children: React.ReactNode;
    backgroundColor?: string;
    pad?: boolean;
    noWrap?: boolean;
};

/**
 * Stable DOM id assigned to the conversation's scrollable wrapper (the
 * `<ScrollView>` inside `ConversationScreen` and `StandaloneLayout`). Used by
 * overlay components that need to portal into the scrolling viewport — most
 * notably `OnboardingPopover`, which calls `getScrollContainer()` and passes
 * the result to `UNSAFE_PortalProvider` so the popover scrolls with the
 * conversation content rather than staying pinned to `document.body`.
 */
export const SCROLL_CONTAINER_ID = 'conversation-scroll-container';

/** Returns the scrollable conversation wrapper, or `null` if not mounted. */
export function getScrollContainer() {
    return document.getElementById(SCROLL_CONTAINER_ID);
}

export function ConversationScreen({ children, backgroundColor, pad = false, noWrap = false }: ConversationScreenProps) {
    const { colors } = useTheme();
    const resolvedBackgroundColor = backgroundColor ?? colors.colorSurfacePrimary;
    const s = useConversationScreenStyles();
    const isInSidebar = useIsInSidebar();
    const [narrow, setNarrow] = useState(false);
    const useWrapper = !isInSidebar && !narrow && !noWrap;
    return (
        <AdaptiveWidth breakpoint={540} setNarrow={setNarrow} style={{ flex: 1, backgroundColor: resolvedBackgroundColor }}>
            {/* nativeID anchors getScrollContainer(); without it, popovers
                portalled here lose scroll-tracking and pin to document.body. */}
            <ScrollView
                nativeID={SCROLL_CONTAINER_ID}
                style={[s.scroller, useWrapper ? null : { backgroundColor: resolvedBackgroundColor }]}
            >
                {useWrapper && <Pad size={40} />}
                <View style={[s.outer, { backgroundColor: resolvedBackgroundColor }]}>
                    <View style={[s.screen, pad ? { paddingHorizontal: 20 } : null, useWrapper ? s.wrapper : null]}>{children}</View>
                </View>
                {useWrapper && <Pad size={48} />}
                <NewPublicFooter />
            </ScrollView>
        </AdaptiveWidth>
    );
}

/** @public */
export const useConversationScreenStyles = createThemedStyles((tokens) => ({
    wrapper: {
        borderRadius: 12,
        borderColor: tokens.colors.colorBorderPrimary,
        borderWidth: 1,
        overflow: 'hidden',
    },
    scroller: {
        flex: 1,
        ...({ overscrollBehavior: 'none' } as any),
    },
    outer: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        flex: 1,
    },
    screen: {
        maxWidth: 600,
        flex: 1,
    },
}));

type AdminScreenProps = {
    capability: string;
    backgroundColor?: string;
};

export function AdminScreen({ capability, backgroundColor, children }: PropsWithChildren<AdminScreenProps>) {
    const { colors } = useTheme();
    const resolvedBackgroundColor = backgroundColor ?? colors.colorSurfacePrimary;
    return (
        <RestrictedScreen capability={capability}>
            <ScrollView style={{ backgroundColor: resolvedBackgroundColor }}>{children}</ScrollView>
        </RestrictedScreen>
    );
}
type AdminScreenHeaderProps = {
    children: React.ReactNode;
};

function AdminScreenHeader({ children }: AdminScreenHeaderProps) {
    const s = useAdminScreenStyles();
    return (
        <View style={s.header}>
            <View style={s.container}>
                <PadBox vert={40}>{children}</PadBox>
            </View>
        </View>
    );
}
type AdminScreenBodyProps = {
    children: React.ReactNode;
};

function AdminScreenBody({ children }: AdminScreenBodyProps) {
    const s = useAdminScreenStyles();
    return <View style={s.container}>{children}</View>;
}

AdminScreen.Header = AdminScreenHeader;
AdminScreen.Body = AdminScreenBody;

/** @public */
export const useAdminScreenStyles = createThemedStyles((tokens) => ({
    header: {
        backgroundColor: tokens.colors.colorSurfaceHighlight,
    },
    container: {
        maxWidth: 1280 + 80,
        paddingLeft: 40,
        paddingRight: 40,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
}));

const NEW_PUBLIC_ORG_NAME = 'New_ Public';

export function NewPublicFooter() {
    const { colors } = useTheme();
    // Read the datastore defensively: this renders inside ConversationScreen,
    // which the skeleton loading screen mounts before the datastore is ready.
    const datastore = useContext(DatastoreContext);
    if (!datastore || datastore.getSiloKey() !== 'np') return null;
    return (
        <Center>
            <HorizBox center>
                <UtilityText label={t('footer.made_by', 'Made by ')} mono type='large' />
                <WebLinkTextButton
                    color={colors.colorTextAccent}
                    mono
                    text={NEW_PUBLIC_ORG_NAME}
                    type='large'
                    url='https://newpublic.org'
                />
            </HorizBox>
            <Pad size={80} />
        </Center>
    );
}
